import React, { useEffect } from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import ActingBodiesDropdown from '../../../components/access/ActingBodiesDropdown';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { documentService } from '../../../services/central-api/document';
import { useForm, useWatch } from 'react-hook-form';
import MyAppText from '../../../components/MyAppText';
import { useAuth } from '../../../context/Auth';

interface DocumentCategoryForm {
    actingBodyId: number;
    actingBodyUnitId: number;
    type: string;
}

export default function DocumentCategoryModal({ onClose, documentCategoryId, isModalVisible }: { onClose: () => void; documentCategoryId?: number; isModalVisible: boolean; }) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const defaultEmptyValues: DocumentCategoryForm = {
        actingBodyId: 0,
        actingBodyUnitId: 0,
        type: '',
    };

    const form = useForm<DocumentCategoryForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });
    const actingBodyId = useWatch({ control: form.control, name: 'actingBodyId' });

    function clearValues() {
        form.reset(defaultEmptyValues);
    }

    async function getCategory(documentCategoryId: number) {
        try {
            const documentCategory = await documentService.getDocumentCategoryById(documentCategoryId);
            form.reset({
                actingBodyUnitId: documentCategory.actingBodyUnitId,
                actingBodyId: documentCategory.actingBodyUnit.actingBody.id,
                type: documentCategory.type
            });
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            clearValues();
            onClose();
        }
    }

    useEffect(() => {
        if (isModalVisible && documentCategoryId) {
            getCategory(documentCategoryId);
        }
    }, [documentCategoryId, isModalVisible]);

    async function formSubmit() {
        try {
            const data = form.getValues();
            if (documentCategoryId) {
                await documentService.updateDocumentCategory({
                    id: documentCategoryId,
                    type: data.type
                });
            } else {
                await documentService.createDocumentCategory(data);
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            clearValues();
            onClose();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    if (!authenticatedUser) {
        return <></>;
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
                clearValues();
            }}
        >
            <View style={styles.modalContainer}>
                <View style={styles.centeredView}>
                    <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: 350 }]}>
                        <MyAppText style={styles.headerText}>{translate(!documentCategoryId ? 'registerDocumentType' : 'updateDocumentType')}</MyAppText>
                        <ActingBodiesDropdown
                            includeAllOption={false}
                            form={form}
                            fieldName='actingBodyId'
                            zIndex={4}
                            required={!documentCategoryId}
                            disabled={!!documentCategoryId || !authenticatedUser.permissions.documents_manage_all_acting_bodies_document_category}
                            semiBoldLabel={true}
                        />
                        <ActingBodiesUnitsDropdown
                            actingBodyId={actingBodyId}
                            form={form}
                            fieldName='actingBodyUnitId'
                            zIndex={3}
                            required={!documentCategoryId}
                            disabled={!!documentCategoryId ||
                                !(
                                    authenticatedUser.permissions.documents_manage_all_acting_bodies_document_category &&
                                    authenticatedUser.permissions.documents_manage_acting_body_units_document_category
                                )}
                            semiBoldLabel={true}
                        />
                        <FormInput
                            label={translate('documentType')}
                            placeholder={translate('documentType')}
                            form={form}
                            fieldName='type'
                            required={true}
                            semiBoldLabel={true}
                        />
                        <FormActions
                            onSubmit={formSubmit}
                            onClose={() => {
                                onClose();
                                clearValues();
                            }}
                            disabled={!form.formState.isValid}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 24
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    headerText: {
        fontSize: 18,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    }
}));
